import React, { useState, useEffect } from "react";
import {
    Box,
    IconButton,
    Typography,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Button,
    Modal,
    Slider,
    Checkbox,
    FormGroup,
    FormControlLabel,
} from "@mui/material";
import SwipeableViews from "react-swipeable-views";
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import apilink from "../globals/Baseurls";
import imgPlaceholder from "./face1.png";

const SuperConnect = () => {
    const navigate = useNavigate();

    // ✅ Unified state for search
    const [searchQuery, setSearchQuery] = useState("");

    // ✅ Unified state for filter modal
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);

    const [ageRange, setAgeRange] = useState([18, 60]);
    const [distance, setDistance] = useState(50);
    const [genderFilter, setGenderFilter] = useState({
        male: true,
        female: true,
        non_binary: true,
        other: true,
    });

    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [fullImageIndex, setFullImageIndex] = useState(0);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem("userSessionData"));
            const token = userData.token;
            const res = await axios.get(`${apilink.link}/fetch-connections`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log(res.data.users);
            setUsers(res.data.users);
            setFilteredUsers(res.data.users);
        } catch (err) {
            console.log(err.response || err);
        }
    };

    const applyFilters = async () => {
        try {
            const genders = Object.keys(genderFilter).filter((g) => genderFilter[g]);

            const dataObj = {
                age_min: ageRange[0],
                age_max: ageRange[1],
                distance_km: distance,
                genders: genders,
            };

            const userData = JSON.parse(localStorage.getItem("userSessionData"));
            const token = userData.token;

            const res = await axios.post(`${apilink.link}/fetch-preferred`, dataObj, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setFilteredUsers(res.data.users);
            setFilterModalOpen(false);
        } catch (err) {
            console.log(err.response || err);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        const filtered = users.filter((u) =>
            u.profile.display_name.toLowerCase().includes(e.target.value.toLowerCase())
        );
        setFilteredUsers(filtered);
    };

    const toggleGender = (g) => {
        setGenderFilter((prev) => ({ ...prev, [g]: !prev[g] }));
    };

    const openProfileModal = (user) => {
        setSelectedProfile(user);
        setFullImageIndex(0);
        setProfileModalOpen(true);
    };

    const closeProfileModal = () => {
        setProfileModalOpen(false);
        setSelectedProfile(null);
    };

    const superChat = async (id) => {
        try {

            const { token } = JSON.parse(localStorage.getItem("userSessionData"));
            const res = await axios.post(
                `${apilink.link}/superchat/${id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const chatObj = {
                conversation_id: res.data.conversation_id,
                user_id:id,
                name : res.data.name,
                photo: res.data.photo,
            }
            localStorage.setItem("chatSessionData", JSON.stringify(chatObj));
            navigate(`/chat`)
        } catch (err) {
            console.error(err.response?.data || err.message);
            alert("Failed to start chat");
        }
    };

    return (
        <Box sx={{ p: 2, width: "100%" }}>
            {/* Top Bar */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        bgcolor: "#eee",
                        mr: 2,
                        "&:hover": { bgcolor: "#ddd" },
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" flex={1}>
                    Super Connect
                </Typography>
            </Box>

            {/* Search + Filter */}
            <Box sx={{ width: "100%", mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search by username"
                    value={searchQuery}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => setFilterModalOpen(true)}
                                >
                                    <FilterListIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                />
            </Box>

            {/* Users Grid */}
            <Grid container spacing={2}>
                {filteredUsers.map((u) => {
                    const primaryPhoto = u.photos?.find((p) => p.is_primary)?.path;

                    return (
                        <Grid
                            item
                            xs={12}   // ✅ FULL WIDTH (1 per row)
                            key={u.id}
                        >
                            <Card
                                sx={{
                                    width: "100%",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    boxShadow: 3,
                                }}
                            >
                                {/* Profile Image */}
                                <CardMedia
                                    component="img"
                                    height="220"
                                    image={
                                        primaryPhoto
                                            ? `${apilink.base_link}/storage/${primaryPhoto}`
                                            : imgPlaceholder
                                    }
                                    alt={u.profile.display_name}
                                    sx={{
                                        objectFit: "cover",
                                        width: "100%",
                                    }}
                                />

                                {/* Content */}
                                <CardContent sx={{ p: 2 }}>
                                    {/* Name + Age */}
                                    <Typography fontWeight="bold" fontSize="18px">
                                        {u.profile.display_name} - {u.profile.age}
                                    </Typography>

                                    {/* Relation Type */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mt: 0.5,
                                            mb: 2,
                                            gap: 1,
                                        }}
                                    >
                                        <SearchIcon sx={{ fontSize: 20, color: "skyblue" }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {u.profile.relation_type}
                                        </Typography>
                                    </Box>

                                    {/* Buttons */}


                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            color: "#115293",
                                            mb: 1.5,
                                            bgcolor: "#fff"
                                        }}
                                        onClick={() => openProfileModal(u)}
                                    >
                                        View Profile
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            color: "#115293",
                                            bgcolor: "#fff"
                                        }}
                                        onClick={() => superChat(u.id)}
                                    >
                                        Send Message
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Profile Modal */}
            <Modal open={profileModalOpen} onClose={closeProfileModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "95%", sm: 500 },
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "90vh",
                    }}
                >
                    {selectedProfile && (
                        <>
                            {/* ✅ Swipeable Photo Viewer */}
                            {selectedProfile.photos?.length > 0 ? (
                                <Box sx={{ position: "relative" }}>
                                    <SwipeableViews
                                        index={fullImageIndex}
                                        onChangeIndex={(i) => setFullImageIndex(i)}
                                        enableMouseEvents
                                    >
                                        {selectedProfile.photos.map((photo, i) => (
                                            <Box
                                                key={i}
                                                component="img"
                                                src={`${apilink.base_link}/storage/${photo.path}`}
                                                alt="profile"
                                                sx={{
                                                    width: "100%",
                                                    height: 320,
                                                    objectFit: "cover",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                    window.open(
                                                        `${apilink.base_link}/storage/${photo.path}`,
                                                        "_blank"
                                                    )
                                                }
                                            />
                                        ))}
                                    </SwipeableViews>

                                    {/* ✅ Photo Counter */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 12,
                                            right: 12,
                                            bgcolor: "rgba(0,0,0,0.6)",
                                            color: "#fff",
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 2,
                                            fontSize: 13,
                                        }}
                                    >
                                        {fullImageIndex + 1} / {selectedProfile.photos.length}
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        height: 250,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        bgcolor: "#f2f2f2",
                                    }}
                                >
                                    <Typography>No Photos</Typography>
                                </Box>
                            )}

                            {/* ✅ Profile Info */}
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {selectedProfile.profile.display_name},{" "}
                                    {selectedProfile.profile.age}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                >
                                    {selectedProfile.profile.relation_type}
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    {selectedProfile.profile.bio}
                                </Typography>

                                {/* ✅ Action Buttons */}


                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={closeProfileModal}
                                >
                                    Close
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Filter Modal */}
            <Modal open={filterModalOpen} onClose={() => setFilterModalOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 400 },
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" mb={2}>
                        Filter Users
                    </Typography>

                    <Typography>Age Range</Typography>
                    <Slider
                        value={ageRange}
                        onChange={(e, val) => setAgeRange(val)}
                        valueLabelDisplay="auto"
                        min={18}
                        max={100}
                        sx={{ mb: 2 }}
                    />

                    <Typography>Distance (km)</Typography>
                    <Slider
                        value={distance}
                        onChange={(e, val) => setDistance(val)}
                        valueLabelDisplay="auto"
                        min={1}
                        max={100}
                        sx={{ mb: 2 }}
                    />

                    <Typography>Gender</Typography>
                    <FormGroup sx={{ mb: 2 }}>
                        {["male", "female", "non_binary", "other"].map((g) => (
                            <FormControlLabel
                                key={g}
                                control={
                                    <Checkbox
                                        checked={genderFilter[g]}
                                        onChange={() => toggleGender(g)}
                                    />
                                }
                                label={g.replace("_", " ")}
                            />
                        ))}
                    </FormGroup>

                    <Button variant="contained" fullWidth onClick={applyFilters}>
                        Apply Filters
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default SuperConnect;
